import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
  FormArray,
} from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { PermissionListInterface } from '@feature/dashboard/user-management/permissions/types/permission-list';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { showValidationMsg } from '@sharedModule/utils/form.utils';
import { Subject, takeUntil } from 'rxjs';
import { ROLE_TYPE } from '../../constant/upsert-role.constant';
import { RoleService } from '../../services/role.service';
import { RoleListInterface } from '../../types/role-list';
import { AddRoleInterface, UpdateRoleInterface } from '../../types/upsert-role';

@Component({
  selector: 'app-upsert-role',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MaterialModule,
    InputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './upsert-role.component.html',
  styleUrl: './upsert-role.component.scss',
})
export class UpsertRoleComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.roles;
  public readonly roleType = ROLE_TYPE;
  upsertRoleFrom!: FormGroup;
  selectedIndex = 0;
  permissionList!: PermissionListInterface[];
  isButtonDisabled = true;
  roleDetails!: RoleListInterface;
  isEdit = false;
  #destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private roleService: RoleService,
    private messageHandlerService: MessageHandlerService
  ) {}

  ngOnInit(): void {
    this.upsertRoleFromInit();
    this.getPermissionList();
    this.isEdit = this._isEdit(this.router.url);
  }

  upsertRoleFromInit(): void {
    this.upsertRoleFrom = new FormGroup({
      [this.attrConstant.name.key]: new FormControl('', [Validators.required]),
      [this.attrConstant.roleType.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.slug.key]: new FormControl(''),
      [this.attrConstant.description.key]: new FormControl(''),
      permission_section: this.fb.array([]),
    });
  }

  getPermissionList(): void {
    this.roleService
      .getPermissionListData()
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            // Only surface active permissions; drop any section left with none.
            this.permissionList = x.response
              .map(section => ({
                ...section,
                permissions: section.permissions.filter(
                  permission => permission.is_active
                ),
              }))
              .filter(section => section.permissions.length > 0);
            this.isButtonDisabled = false;
          }
        },
      });
  }

  onChange(
    event: { checked: boolean; source: { value: string } },
    data: PermissionListInterface
  ): void {
    const permissions = this.upsertRoleFrom.get(
      'permission_section'
    ) as FormArray;
    if (event.checked) {
      permissions.push(
        this.fb.group({
          permission_section_id: new FormControl(data.permission_section_id),
          permission_id: new FormControl(event.source.value),
        })
      );
    } else {
      const i = permissions.controls.findIndex(x => {
        return x.value.permission_id === event.source.value;
      });
      permissions.removeAt(i);
    }
  }

  private _isEdit = (url: string): boolean => {
    const endUrl: string[] = url.split('/');
    if (endUrl[3] == 'edit') {
      this.getRoleDetails(endUrl[4]);
      return true;
    } else {
      return false;
    }
  };

  getRoleDetails(roleId: string): void {
    this.roleService
      .getRoleDetails({ role_id: roleId })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.roleDetails = x.response;
            this.upsertRoleFrom.patchValue({
              name: this.roleDetails.name,
              slug: this.roleDetails.slug,
              description: this.roleDetails.description,
              role_type: this.roleDetails.role_type,
            });
          }
        },
      });
  }

  assignPermission(roleDetails: RoleListInterface): void {
    this.permissionList.map(section => {
      section.permissions.map(per => {
        per.isChecked = false;
        const permissionFlag = roleDetails.permission_section.find(
          perm => perm.permission_id === per.permission_id
        );
        if (permissionFlag) {
          per.isChecked = true;
          const permissions = this.upsertRoleFrom.get(
            'permission_section'
          ) as FormArray;
          permissions.push(
            this.fb.group({
              permission_section_id: section.permission_section_id,
              permission_id: per.permission_id,
            })
          );
        }
        return per;
      });
      return section;
    });
  }

  onUpsertRole(): void {
    if (this.upsertRoleFrom.valid) {
      if (this.isEdit)
        this.updateRole({
          role_id: this.roleDetails.role_id,
          ...this.upsertRoleFrom.value,
        });
      else this.addRole(this.upsertRoleFrom.value);
    }
  }

  addRole(formValue: AddRoleInterface): void {
    this.roleService
      .addRole(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/user-management/role/list']);
          }
        },
      });
  }

  updateRole(formValue: UpdateRoleInterface): void {
    this.roleService
      .updateRole(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/user-management/role/list']);
          }
        },
      });
  }

  tabClick(event: { index: number }): void {
    if (event.index === 0) this.selectedIndex = 0;
  }

  nextStep(): void {
    showValidationMsg(this.upsertRoleFrom);
    if (this.upsertRoleFrom.valid) {
      this.selectedIndex = 1;
      if (this.isEdit) this.assignPermission(this.roleDetails);
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
